import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BRAND_ASSETS } from '../../core/constants/brand-assets';

@Component({
  selector: 'app-verify-2fa',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './verify-2fa.component.html',
  styleUrl: './verify-2fa.component.scss'
})
export class Verify2faComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;
  verifyForm: FormGroup;
  loading = false;
  errorMessage = '';
  userEmail = '';
  useBackupCode = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private http: HttpClient
  ) {
    this.verifyForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Get email from route state (passed from login page)
    const navigation = this.router.getCurrentNavigation();
    this.userEmail = navigation?.extras?.state?.['email'] || '';

    if (!this.userEmail) {
      // If no email in state, redirect back to login
      this.router.navigate(['/login']);
    }
  }

  toggleBackupCode(): void {
    this.useBackupCode = !this.useBackupCode;
    this.errorMessage = '';
    this.verifyForm.reset();

    // Update validators based on mode
    if (this.useBackupCode) {
      this.verifyForm.controls['code'].setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(8)]);
    } else {
      this.verifyForm.controls['code'].setValidators([Validators.required, Validators.minLength(6)]);
    }
    this.verifyForm.controls['code'].updateValueAndValidity();
  }

  async onSubmit(): Promise<void> {
    if (this.verifyForm.invalid) {
      this.verifyForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const { code } = this.verifyForm.value;

      // Call backend 2FA verification endpoint
      const response = await this.http.post<{ success: boolean; message: string }>(
        `${environment.apiUrl}/auth/2fa/verify`,
        {
          email: this.userEmail,
          code: code.trim()
        }
      ).toPromise();

      if (response?.success) {
        // 2FA verification successful, redirect to dashboard
        this.router.navigate(['/dashboard']);
      } else {
        this.errorMessage = 'Invalid verification code. Please try again.';
      }
    } catch (error: any) {
      console.error('2FA verification error:', error);

      if (error.status === 401) {
        this.errorMessage = 'Invalid verification code. Please try again.';
      } else if (error.status === 400) {
        this.errorMessage = error.error?.error || '2FA is not enabled for this account.';
      } else {
        this.errorMessage = 'An error occurred during verification. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  goBackToLogin(): void {
    this.router.navigate(['/login']);
  }
}
